import { wrap } from '@popmotion/popcorn';
import React, { PropsWithChildren } from 'react';
import { animate, motion, PanInfo, Transition, useMotionValue } from 'framer-motion';
import useResizeObserver from '@react-hook/resize-observer';
import { CSSObject } from '@emotion/react';
import Button from '../button';
import { useGridContext } from '../grid/context';

interface SliderContext {
    page: number
    direction: number
    paginate: (newDirection: number) => void
    jumpTo: (index: number) => void
    slideCount: number
    setPageBounds: (args: { lastPage: number; infinite: boolean }) => void
}

interface SliderInstanceContext {
    slidesPerViewMap: Record<string, number>;
    infinite?: boolean;
}

const SliderContext = React.createContext<SliderContext>(null as unknown as SliderContext);
const SliderInstanceContext = React.createContext<SliderInstanceContext>(null as unknown as SliderInstanceContext);

export const useSliderContext = (): SliderContext => React.useContext(SliderContext);

export const useSliderInstanceContext = (): SliderInstanceContext => React.useContext(SliderInstanceContext);

interface NotchedSlider {
    children: React.ReactNode|React.ReactNode[]
    autoPlay?: number
    slideCount: number
}

const makeSlideSizeCss = (
    map: Record<string, number>,
    entries: GridBpEntry[],
    slideIndex: number,
    rangeIndex: number,
    shouldRender: boolean,
): CSSObject => {
    const firstTokenWithMap = entries.find(([t]) => t in map)?.[0];
    const baseSpv = (firstTokenWithMap ? map[firstTokenWithMap] : Object.values(map)[0]) ?? 1;

    const wPercent = (spv: number): string => `${100 / Math.max(1, spv)}%`;
    const lPercent = (spv: number): string => `${slideIndex * (100 / Math.max(1, spv))}%`;

    return entries.reduce((acc, [token, bp]) => {
        if (!(token in map) || !bp?.query) return acc;

        const spv = Math.max(1, map[token]);
        return {
            ...acc,
            [bp.query]: {
                width: wPercent(spv),
                minWidth: wPercent(spv),
                maxWidth: wPercent(spv),
                position: shouldRender ? 'absolute' : 'relative',
                left: shouldRender ? lPercent(spv) : undefined,
                marginLeft: (!shouldRender && rangeIndex === 0) ? lPercent(spv) : undefined,
            },
        };
    }, {
        width: wPercent(baseSpv),
        minWidth: wPercent(baseSpv),
        maxWidth: wPercent(baseSpv),
        position: shouldRender ? 'absolute' : 'relative',
        left: shouldRender ? lPercent(baseSpv) : undefined,
        marginLeft: (!shouldRender && rangeIndex === 0) ? lPercent(baseSpv) : undefined,
    } as CSSObject);
};
interface NotchedSliderInstance {
    children: React.ReactNode | React.ReactNode[];
    infinite?: boolean;
    slidesPerView: Record<string, number>; // e.g. { mb: 1, tb: 2, dt: 3 }
}

export const NotchedSliderInstance = ({ infinite, slidesPerView, children }: NotchedSliderInstance): JSX.Element => {
    const value = React.useMemo(() => ({
        infinite: !!infinite,
        slidesPerViewMap: slidesPerView,
    }), [infinite, slidesPerView]);

    return (
        <SliderInstanceContext.Provider value={value}>
            {children}
        </SliderInstanceContext.Provider>
    );
};

const NotchedSlider = ({ children, autoPlay, slideCount }: NotchedSlider): JSX.Element => {
    const [[page, direction], setPage] = React.useState([0, 0]);
    const boundsRef = React.useRef<{ lastPage: number; infinite: boolean }>({ lastPage: 0, infinite: false });

    const setPageBounds = React.useCallback((b: { lastPage: number; infinite: boolean }) => {
        boundsRef.current = b;
    }, []);

    const paginate = React.useCallback((newDirection: number) => {
        setPage(([p]) => {
            const { lastPage, infinite } = boundsRef.current;
            if (infinite) return [p + newDirection, newDirection];
            const clamped = Math.min(Math.max(0, p + newDirection), lastPage);
            return [clamped, newDirection];
        });
    }, []);

    const jumpTo = React.useCallback((newPage: number) => {
        setPage(([p]) => {
            const { lastPage, infinite } = boundsRef.current;
            if (infinite) return [newPage, newPage > p ? 1 : -1];
            const clamped = Math.min(Math.max(0, newPage), lastPage);
            return [clamped, clamped > p ? 1 : -1];
        });
    }, []);

    React.useEffect(() => {
        let timer: NodeJS.Timeout;
        if (autoPlay) {
            timer = setTimeout(() => {
                paginate(1);
            }, autoPlay * 1000);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [autoPlay, paginate, page]);

    const value = React.useMemo(() => ({
        page,
        direction,
        paginate,
        jumpTo,
        slideCount,
        setPageBounds,
    }), [page, direction, paginate, jumpTo, slideCount, setPageBounds]);

    return (
        <SliderContext.Provider value={value}>
            {children}
        </SliderContext.Provider>
    );
};

type GridBp = { query: string; columns: number; gap: number };
type GridBpEntry = [string, GridBp];
type Match = { token: string; query: string };

const makePrevArrowDisableCss = (
    spvMap: Record<string, number>,
    entries: GridBpEntry[],
    infinite: boolean,
): CSSObject => {
    if (infinite) return {};
    return entries.reduce((acc, [token, bp]) => {
        const spv = spvMap[token];
        if (!bp?.query || !spv) return acc;

        return {
            ...acc,
            [bp.query]: {
                '&[data-page="0"] [data-prev]': {
                    pointerEvents: 'none',
                    opacity: 0.5,
                    cursor: 'default',
                },
            },
        };
    }, {} as CSSObject);
};

const makeNextArrowDisableCss = (
    slideCount: number,
    spvMap: Record<string, number>,
    entries: GridBpEntry[],
    infinite: boolean,
): CSSObject => {
    if (infinite) return {};
    return entries.reduce((acc, [token, bp]) => {
        const spv = spvMap[token];
        if (!bp?.query || !spv) return acc;

        const pages = Math.max(1, Math.ceil(slideCount / Math.max(1, spv)));
        const lastPage = pages - 1;

        return {
            ...acc,
            [bp.query]: {
                [`&[data-page="${lastPage}"] [data-next]`]: {
                    pointerEvents: 'none',
                    opacity: 0.5,
                    cursor: 'default',
                },
            },
        };
    }, {} as CSSObject);
};

const SliderLeftButton = ({ children }: PropsWithChildren): JSX.Element => {
    const { paginate, page } = useSliderContext();
    const { slidesPerViewMap, infinite } = useSliderInstanceContext();
    const { sortedBreakpoints } = useGridContext();

    const prevDisableCss = React.useMemo(
        () => makePrevArrowDisableCss(slidesPerViewMap, sortedBreakpoints, !!infinite),
        [slidesPerViewMap, sortedBreakpoints, infinite],
    );

    return (
        <div
            data-page={page}
            css={prevDisableCss}
            aria-hidden="true"
        >
            <Button
                type="button"
                aria-label="Previous"
                data-prev
                onClick={() => paginate(-1)}
            >
                {children}
            </Button>
        </div>
    );
};

const SliderRightButton = ({ children }: PropsWithChildren): JSX.Element => {
    const { paginate, page, slideCount } = useSliderContext();
    const { slidesPerViewMap, infinite } = useSliderInstanceContext();
    const { sortedBreakpoints } = useGridContext();

    const nextDisableCss = React.useMemo(
        () => makeNextArrowDisableCss(slideCount, slidesPerViewMap, sortedBreakpoints, !!infinite),
        [infinite, slideCount, slidesPerViewMap, sortedBreakpoints],
    );

    return (
        <div
            data-page={page}
            css={nextDisableCss}
            aria-hidden="true"
        >
            <Button
                type="button"
                aria-label="Next"
                data-next
                onClick={() => paginate(1)}
            >
                {children}
            </Button>
        </div>
    );
};

export const getSpv = (
    map: Record<string, number>,
    entries: GridBpEntry[],
    fallback = 1,
): number => {
    const isCatchAll = (q?: string): boolean => {
        if (!q) return true;
        const s = q.trim().toLowerCase();
        return s === 'screen' || s === 'only screen' || s === 'all' || s === 'only all';
    };

    const matches: Match[] = entries.map(([token, bp]) => {
        const raw = bp?.query?.trim() || '';
        const q = raw.startsWith('@media') ? raw.replace(/^@media\s*/i, '').trim() : raw;
        return [token, { ...bp, query: q }] as GridBpEntry;
    })
        .filter(([token, bp]) => Boolean(bp?.query) && token in map)
        .filter(([, bp]) => window.matchMedia((bp.query as string)).matches)
        .map(([token, bp]) => ({ token, query: bp.query as string }));

    const firstSpecific = matches.find(m => !isCatchAll(m.query));
    const chosen = firstSpecific ?? matches[0];

    if (chosen) return Math.max(1, map[chosen.token]!);

    const firstVal = Object.values(map)[0];
    return Math.max(1, firstVal ?? fallback);
};

interface Slides {
    children: React.ReactNode[]
    className?: string
    slidesPerView?: number
    centered?: boolean
    overflowVisible?: boolean
    masksLeft?: boolean
    masksRight?: boolean
}

const transition: Transition = {
    type: 'spring',
    bounce: 0,
};

const Slides = ({
    children,
    className,
    centered,
    overflowVisible,
    masksLeft,
    masksRight,
}: Slides): JSX.Element => {
    const { page, paginate, jumpTo, slideCount, setPageBounds } = useSliderContext();
    const { infinite, slidesPerViewMap } = useSliderInstanceContext();
    const { sortedBreakpoints: outerBreakpoints } = useGridContext();

    const largestSpv = Object.values(slidesPerViewMap).reduce((acc, val) => Math.max(acc, val), 1);

    if (centered && !infinite) throw new Error('Slides cannot be centered and non-infinite');

    const [activated, setActivated] = React.useState(false);
    const slides = React.Children.toArray(children);
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    if (slides.length !== slideCount) throw new Error('NotchedSlider.Slides: children must equal slideCount');

    const [dimensions, setMaxDimensions] = React.useReducer((
        prev: { height: number; width: number; heightIndex: number; widthIndex: number },
        action: { element: HTMLDivElement | null; index: number },
    ) => {
        if (action.index === 0) {
            return { height: 0, width: 0, heightIndex: 0, widthIndex: 0 };
        }
        if (action.element && action.index > 0) {
            const updates = {
                height: prev.height,
                heightIndex: prev.heightIndex,
                width: prev.width,
                widthIndex: prev.widthIndex,
            };

            const { height } = action.element.getBoundingClientRect();
            if (prev?.height < height || action.index <= prev.heightIndex) {
                updates.height = height;
                updates.heightIndex = action.index;
            }

            if (action.element.firstElementChild) {
                const { width } = (action.element.firstElementChild as HTMLElement).getBoundingClientRect();
                if (prev?.width < width || action.index <= prev.widthIndex) {
                    updates.width = width;
                    updates.widthIndex = action.index;
                }
            }
            return updates;
        }
        return prev;
    }, { height: 0, width: 0, heightIndex: 0, widthIndex: 0 });

    const { height: minHeight } = dimensions;

    const x = useMotionValue(0);
    const [containerW, setContainerW] = React.useState(0);

    const totalPages = Math.max(1, Math.ceil(slides.length / Math.max(1, largestSpv)));
    const lastPage = totalPages - 1;

    const totalUnits = infinite
        ? slides.length
        : Math.max(1, Math.ceil(slides.length / Math.max(1, largestSpv)));

    const lastUnit = totalUnits - 1;
    const renderIndex = infinite
        ? page
        : Math.min(Math.max(0, page), lastUnit);

    React.useEffect(() => {
        setPageBounds({
            lastPage: infinite ? 0 : lastUnit,
            infinite: !!infinite,
        });
    }, [lastPage, infinite, setPageBounds, lastUnit]);

    const newX = React.useCallback((): number => {
        const containerWidth = containerRef.current?.clientWidth ?? 0;
        if (!containerWidth) return 0;

        const spv = getSpv(slidesPerViewMap, outerBreakpoints);

        if (infinite) {
            // one slide wide
            const slideWidth = containerWidth / Math.max(1, spv);
            return -(renderIndex * slideWidth);
        }

        // one viewport wide
        const offset = Math.min(Math.max(0, renderIndex * containerWidth), lastUnit * containerWidth);
        return -offset;
    }, [slidesPerViewMap, outerBreakpoints, infinite, renderIndex, lastUnit]);

    const rafRef = React.useRef<number>(0);

    useResizeObserver<HTMLDivElement>(containerRef, (entry: ResizeObserverEntry | undefined): void => {
        if (!entry) return;
        const w = entry.contentRect.width || containerRef.current?.clientWidth || 0;
        if (w && w !== containerW) setContainerW(w);
    });

    useResizeObserver<HTMLDivElement>(scrollRef, (entry: ResizeObserverEntry | undefined): void => {
        if (!entry) return;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            const controls = animate(x, newX(), { type: 'tween', duration: 0 });
            controls.stop();
        });
    });

    const paginateBounded = (dir: number): void => {
        if (infinite) {
            paginate(dir);
            return;
        }
        const next = page + dir;
        const clamped = Math.min(Math.max(0, next), lastPage);
        if (clamped === page) {
            animate(x, newX(), transition);
        } else {
            paginate(dir);
        }
    };

    const handleEndDrag = (_: unknown, dragProps: PanInfo): void => {
        const spv = getSpv(slidesPerViewMap, outerBreakpoints);
        containerRef.current?.classList.remove('dragging');
        if (!dragProps) return;

        const { offset, velocity } = dragProps;
        if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
            animate(x, newX(), transition);
            return;
        }
        const threshold = (containerW || 0) / spv / 4;
        if (offset.x > threshold) paginateBounded(-1);
        else if (offset.x < -threshold) paginateBounded(1);
        else animate(x, newX(), transition);
    };

    React.useEffect(() => {
        if (!infinite && page > lastPage) {
            jumpTo(lastPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastPage, infinite]);

    React.useEffect(() => {
        const ctrls = animate(x, newX(), { type: 'tween', duration: 0 });
        return ctrls.stop;
    }, [containerW, slides.length, infinite, x, newX]);

    React.useEffect(() => {
        if (minHeight > 0 && !activated) setActivated(true);
    }, [minHeight, activated]);

    React.useEffect(() => {
        if (!activated) return () => {};
        const ctrls = animate(x, newX(), transition);
        return ctrls.stop;
    }, [renderIndex, activated, x, newX]);

    const range = React.useMemo(
        () => (infinite
            ? [...new Array((largestSpv * 2) + 4 + (largestSpv % 2 === 0 ? 0 : 1))].map((_, idx) => idx - (largestSpv + 2))
            : [...new Array(slides.length)].map((_, idx) => idx)),
        [infinite, largestSpv, slides.length],
    );

    const centerSlideOffset = centered ? Math.ceil(largestSpv / 2) : 0;
    const renderedMap: Record<string, true> = {};

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
        <div
            ref={scrollRef}
            role="list"
            onClick={() => { if (!activated) setActivated(true); }}
            css={{
                width: '100%',
                position: 'relative',
                display: 'flex',
                overflow: overflowVisible ? undefined : 'hidden',
                userSelect: 'none',
            }}
            className={className}
            style={{ minHeight: activated ? `${minHeight}px` : undefined }}
        >
            <motion.div
                ref={containerRef}
                css={{
                    width: '100%',
                    display: 'flex',
                    flexFlow: 'row nowrap',
                    flex: 'none',
                    transform: 'none!important',
                }}
            >
                {range.map((rangeValue, idx) => {
                    if (infinite) {
                        const index = rangeValue + renderIndex;
                        const modulo = index % slides.length;
                        const imageIndex = modulo < 0 ? slides.length + modulo : modulo;
                        const shouldRender = minHeight !== 0 && activated;

                        const shouldMaskLeft = masksLeft && rangeValue < 0;
                        const shouldMaskRight = masksRight && rangeValue > (largestSpv - 1);

                        const wrappedIndex = wrap(0, slides.length, centered ? imageIndex - centerSlideOffset + 1 : imageIndex);

                        const isAriaHidden = renderedMap[wrappedIndex];
                        renderedMap[wrappedIndex] = true;

                        const slideSizeCss = makeSlideSizeCss(
                            slidesPerViewMap,
                            outerBreakpoints,
                            index,
                            idx,
                            shouldRender,
                        );

                        return (
                            <motion.div
                                data-slide
                                key={index}
                                aria-hidden={isAriaHidden}
                                ref={e => setMaxDimensions({ element: e, index })}
                                css={{ height: '100%', ...slideSizeCss }}
                                style={{ x }}
                                drag="x"
                                draggable
                                dragElastic={1}
                                onDragEnd={handleEndDrag}
                                onDragStart={() => {
                                    containerRef.current?.classList.add('dragging');
                                }}
                            >
                                <span
                                    css={{
                                        '.dragging &': { pointerEvents: 'none' },
                                        opacity: (shouldMaskLeft || shouldMaskRight) ? 0 : 1,
                                        transition: 'opacity 0.25s',
                                    }}
                                    data-active={centered ? rangeValue - centerSlideOffset + 1 === 0 : rangeValue === 0}
                                >
                                    {slides[wrappedIndex]}
                                </span>
                            </motion.div>
                        );
                    }

                    const index = rangeValue;
                    const windowStart = renderIndex * largestSpv;
                    const relative = index - windowStart;
                    const shouldMaskLeft = !!masksLeft && relative < 0;
                    const shouldMaskRight = !!masksRight && relative > (largestSpv - 1);

                    const modulo = index % slides.length;
                    const imageIndex = modulo < 0 ? slides.length + modulo : modulo;
                    const shouldRender = minHeight !== 0 && activated;

                    const wrappedIndex = wrap(0, slides.length, centered ? imageIndex - centerSlideOffset + 1 : imageIndex);

                    const slideSizeCss = makeSlideSizeCss(
                        slidesPerViewMap,
                        outerBreakpoints,
                        index,
                        idx,
                        shouldRender,
                    );

                    return (
                        <motion.div
                            key={index}
                            data-slide
                            draggable
                            ref={e => setMaxDimensions({ element: e, index })}
                            css={{ height: '100%', ...slideSizeCss }}
                            style={{ x }}
                            drag="x"
                            dragElastic={1}
                            onDragEnd={handleEndDrag}
                            onDragStart={() => {
                                containerRef.current?.classList.add('dragging');
                            }}
                        >
                            <span
                                css={{
                                    '.dragging &': { pointerEvents: 'none' },
                                    opacity: (shouldMaskLeft || shouldMaskRight) ? 0 : 1,
                                    transition: 'opacity 0.25s',
                                }}
                                data-active={centered ? rangeValue - centerSlideOffset + 1 === 0 : rangeValue === 0}
                            >
                                {slides[wrappedIndex]}
                            </span>
                        </motion.div>
                    );
                })}
            </motion.div>
        </div>
    );
};

NotchedSlider.Slides = Slides;
NotchedSlider.SliderLeftButton = SliderLeftButton;
NotchedSlider.SliderRightButton = SliderRightButton;

export default NotchedSlider;
